import { Logger } from "../services";

/* eslint-disable no-new */
export function isValidUrl(url: string): boolean {
  try {
    new URL(url);

    return true;
  } catch (err) {
    console.error(err);

    return false;
  }
}

// react-dropzone support filetype handling function
export const getAcceptTypes = (supportData: string) => {
  const accept: { [key: string]: string[] } = {};
  const fileTypes = supportData.split(',');

  fileTypes.forEach(type => {
    switch (type) {
      case 'ftl':
        accept['application/ftl'] = ['.ftl'];
        break;
      case 'pdf':
        accept['application/pdf'] = ['.pdf'];
        break;
      case 'doc':
      case 'docx':
        accept['application/msword'] = ['.doc', '.docx'];
        break;
      case 'image':
        accept['image/*'] = ['.png', '.gif', '.jpeg', '.jpg'];
        break;
      default:
        break;
    }
  });

  return accept;
};

export const generateRandomGradient = () => {
  const gradients = [
    'linear-gradient(225deg, #E99EF9 0%, #AE53E4 100%)',
    'linear-gradient(225deg, #26ECB0 0%, #0FCC93 100%), linear-gradient(225deg, #E99EF9 0%, #AE53E4 100%)',
    'linear-gradient(225deg, #9EADFF 0%, #6C7BEA 100%)',
    'linear-gradient(225deg, #58DDE0 0%, #2FC5CA 100%)',
  ];

  return gradients[Math.floor(Math.random() * 3)];
};

export function hasSelectedItem(items: any[]): boolean {
  return items.some(item => item?.selected === true);
}

export function findSelectedItem(items: any[]): any | null {
  for (const item of items) {
    if (item.selected) {
      return item;
    }
  }

  return null;
}

export const emailRegex = /^[\w\-+.]+@([\w-]+\.)+[\w-]{2,}$/;

export const amountRegex = /^\d*\.?\d{0,2}$/;

export function countLines(elementId: string): number {
  if (!elementId) return 0;

  const el = document.getElementById(elementId);

  if (!el) {
    console.error(`Element with id '${elementId}' not found.`);

    return 0;
  }

  const divHeight = el.offsetHeight;
  const lineHeightStr = window.getComputedStyle(el).lineHeight;
  const lineHeight = parseFloat(lineHeightStr);

  if (!lineHeight || isNaN(lineHeight)) {
    console.error('Line height is not properly defined.');

    return 0;
  }

  return Math.ceil(divHeight / lineHeight);
}

interface HandleAddCodeAutomaticallyHelperProps {
  code: string;
  email: string;
  dispatch: any;
  addFlashNotification: any;
}

export const handleAddCodeAutomaticallyHelper = ({
  code,
  email,
  dispatch,
  addFlashNotification,
}: HandleAddCodeAutomaticallyHelperProps) => {
  if (code && email) {
    // Format: otpauth://totp/issuer:email?secret=secret_key&issuer=issuer_name
    const otpauthUrl = `otpauth://totp/i2iConnect:${encodeURIComponent(
      email,
    )}?secret=${encodeURIComponent(code)}&issuer=i2iConnect`;

    const isMobileUA =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      );

    if (!isMobileUA) {
      dispatch(
        addFlashNotification({
          message: `This feature is optimized for mobile devices with authenticator apps installed.\n\nYour setup code:\n\n\nYou can manually enter this code in your authenticator app.`,
          isError: false,
          duration: 6000,
        }),
      );

      return;
    }

    let appOpened = false;

    const markAppOpened = () => {
      appOpened = true;
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        markAppOpened();
      }
    };

    // iOS Safari does not reliably fire visibilitychange on repeat navigations
    // to the same URL scheme — window blur is a more consistent signal.
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('blur', markAppOpened);

    window.location.href = otpauthUrl;

    setTimeout(() => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('blur', markAppOpened);

      if (!appOpened) {
        dispatch(
          addFlashNotification({
            message: `Authenticator app not found.\n\nPlease ensure you have an authenticator app (Google Authenticator, Authy, etc.) installed and try again.\n\n`,
            isError: true,
            duration: 6000,
          }),
        );
      }
    }, 2000);
  }
};

// Strips the `officeImage:` entry from a `;`-delimited office address string
export const removeOfficeImageFromAddress = (officeAddress: any) => {
  if (typeof officeAddress !== 'string') return officeAddress;

  return officeAddress
    .split(';')
    .filter(entry => !entry.trimStart().startsWith('officeImage:'))
    .join(';');
};

export const extractAuthorities = (accessToken: string) => {
  if (!accessToken) return [];

  try {
    const parts = accessToken.split('.');

    if (parts.length !== 3) return [];

    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '=',
    );
    const decoded = JSON.parse(
      decodeURIComponent(
        atob(padded)
          .split('')
          .map(c => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
          .join(''),
      ),
    );

    return decoded.authorities || [];
  } catch (error) {
    Logger.error('Error decoding authorities from token:', error);

    return [];
  }
};